// src/runtime/theme.js
const KEY = 'crusher_prefs';
const EVT = 'crusher:themechange';
const SCHEMA_VERSION = 1;
const HAS_BROWSER_RUNTIME = typeof window !== 'undefined' && typeof document !== 'undefined';
let schemaWarned = false;

function requireBrowserRuntime() {
  if (!HAS_BROWSER_RUNTIME) {
    throw new Error('crusher-ui-kit/runtime requires a browser environment (window/document).');
  }
}

function readPrefsState() {
  if (!HAS_BROWSER_RUNTIME || typeof localStorage === 'undefined') return { prefs: {}, readOnly: false };
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return { prefs: {}, readOnly: false };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return { prefs: {}, readOnly: false };
    if (parsed.v == null) {
      parsed.v = SCHEMA_VERSION;
      return { prefs: parsed, readOnly: false };
    }
    if (typeof parsed.v === 'number' && parsed.v > SCHEMA_VERSION) {
      if (!schemaWarned) {
        schemaWarned = true;
        console.warn('crusher_prefs schema v=' + parsed.v + ' ahead of runtime v=' + SCHEMA_VERSION + '; running in read-only mode');
      }
      return { prefs: parsed, readOnly: true };
    }
    return { prefs: parsed, readOnly: false };
  } catch { return { prefs: {}, readOnly: false }; }
}

function readPrefs() {
  return readPrefsState().prefs;
}

function writePrefs(p) {
  if (!HAS_BROWSER_RUNTIME || typeof localStorage === 'undefined') return;
  const { readOnly } = readPrefsState();
  if (readOnly) return;
  try { localStorage.setItem(KEY, JSON.stringify({ ...p, v: SCHEMA_VERSION })); } catch {}
}

function emitThemeChange(detail = {}) {
  if (!HAS_BROWSER_RUNTIME) return;
  window.dispatchEvent(new CustomEvent(EVT, { detail, bubbles: true }));
}

function getThemeLock() {
  if (!HAS_BROWSER_RUNTIME) return '';
  return document.documentElement.getAttribute('data-theme-lock') || '';
}

// Locked pages render the locked dialect in memory only. Storage is never
// modified, other tabs / sites on the same origin keep their saved theme.
// Saved brand color is preserved across lock changes (brand is per-user,
// not per-theme).
function applyThemeLock(prefs = readPrefs()) {
  const theme = getThemeLock();
  if (!theme) return false;
  document.documentElement.setAttribute('data-theme', theme);
  if (prefs.brand) {
    document.documentElement.style.setProperty('--crusher-color-brand-primary', prefs.brand);
  }
  return true;
}

export function getTheme() {
  requireBrowserRuntime();
  return document.documentElement.getAttribute('data-theme') || 'glass';
}
export function getMode() {
  requireBrowserRuntime();
  return document.documentElement.getAttribute('data-mode') || 'light';
}
export function setTheme(name) {
  requireBrowserRuntime();
  const prev = getTheme();
  document.documentElement.setAttribute('data-theme', name);
  // Locked pages render the requested theme in memory but never persist it.
  if (!getThemeLock()) {
    const prefs = readPrefs(); prefs.theme = name; writePrefs(prefs);
  }
  emitThemeChange({ prevTheme: prev, theme: name, mode: getMode() });
}
export function setMode(mode /* 'light' | 'dark' */) {
  requireBrowserRuntime();
  const prev = getMode();
  document.documentElement.setAttribute('data-mode', mode);
  const prefs = readPrefs(); prefs.mode = mode; writePrefs(prefs);
  emitThemeChange({ prevMode: prev, mode, theme: getTheme() });
}
export function toggleMode() {
  setMode(getMode() === 'dark' ? 'light' : 'dark');
}
export function setDensity(density /* 'compact' | 'cozy' | 'comfortable' */) {
  requireBrowserRuntime();
  document.documentElement.setAttribute('data-density', density);
  const prefs = readPrefs(); prefs.density = density; writePrefs(prefs);
  emitThemeChange({ density, theme: getTheme(), mode: getMode() });
}
export function lockTheme(name) {
  requireBrowserRuntime();
  document.documentElement.setAttribute('data-theme-lock', name);
  const prefs = readPrefs();
  const prev = getTheme();
  applyThemeLock(prefs);
  emitThemeChange({ prevTheme: prev, theme: name, mode: getMode(), locked: true });
}

// Init on import
(function init() {
  if (!HAS_BROWSER_RUNTIME) return;
  const prefs = readPrefs();
  const hasThemeLock = applyThemeLock(prefs);
  if (!hasThemeLock && prefs.theme) document.documentElement.setAttribute('data-theme', prefs.theme);
  if (prefs.mode)  document.documentElement.setAttribute('data-mode', prefs.mode);
  if (prefs.density) document.documentElement.setAttribute('data-density', prefs.density);

  // Fall back to system dark only when there is no signal from either prefs
  // or the DOM. Static tools using tool-shell.js pre-set data-mode="light", so
  // they opt out of the system-pref override; consumer sites without an
  // explicit attribute still get system-dark behavior.
  if (!prefs.mode
      && !document.documentElement.hasAttribute('data-mode')
      && window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
    document.documentElement.setAttribute('data-mode', 'dark');
  }

  emitThemeChange({ theme: getTheme(), mode: getMode(), density: document.documentElement.getAttribute('data-density') || 'comfortable' });
})();
